import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { COUNTRIES, COUNTRY_LIST, NO_COUNTRY, areaCountry } from "../../config/countries";
import type { CountryCode } from "../../config/countries";
import { DOCS_LINKS } from "../../config/links";
import { geoTypeLabel } from "../../config/products";
import { useReadOnly } from "../../hooks/useReadOnly";
import type { Section } from "../../pages/home/homeSection";
import { projectsApi } from "../../services/projectsApi";
import type { Project } from "../../services/projectsApi";
import { userMapsApi } from "../../services/userMapsApi";
import type { LoadGeometriesResponse } from "../../services/userMapsApi";
import { projectDataElsewhere, projectRealtimeAvailability } from "../../utils/realtimeEnv";
import { pluralize } from "../../utils/plural";
import Spinner from "../common/Spinner";
import { LandingCard, LandingNewCard } from "./LandingCard";
import { LANDING_SAMPLES } from "./landingSamples";

type Area = LoadGeometriesResponse["maps"][number];
type Load<T> = { status: "loading" } | { status: "failed" } | { status: "ready"; items: T[] };

/** How many items a section shows, before its "+ New" card. */
const MAX_ITEMS = 6;

const QUICK_TILE =
  "block rounded-2xl border border-paper-300/70 bg-surface p-4 text-left transition-colors hover:border-brand-500/40 hover:bg-paper-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-500";

const stamp = (p: Project): number => {
  const t = Date.parse(p.updatedAt ?? p.createdAt ?? "");
  return Number.isNaN(t) ? -Infinity : t;
};

/** Newest first; projects without a timestamp go last in the order the API gave them. */
function newestFirst(projects: Project[]): Project[] {
  return projects
    .map((p, i) => ({ p, i, t: stamp(p) }))
    .sort((a, b) => (a.t === b.t ? a.i - b.i : b.t > a.t ? 1 : -1))
    .map((x) => x.p);
}

const QuickTile: React.FC<{ title: string; hint: string }> = ({ title, hint }) => (
  <>
    <span className="block text-sm font-semibold text-ink">{title}</span>
    <span className="mt-0.5 block text-xs text-ink-muted">{hint}</span>
  </>
);

interface GridProps {
  id: string;
  title: string;
  noun: string;
  total: number;
  load: Load<unknown>;
  onSeeAll: () => void;
  onRetry: () => void;
  emptyText: string;
  newLabel: string | null;
  onNew: () => void;
  renderItems: () => React.ReactNode;
}

/** One landing section: heading, count, "See all", and its grid of cards or the sample cards. */
const Grid: React.FC<GridProps> = ({
  id,
  title,
  noun,
  total,
  load,
  onSeeAll,
  onRetry,
  emptyText,
  newLabel,
  onNew,
  renderItems,
}) => {
  const headingId = `landing-${id}`;
  return (
    <section aria-labelledby={headingId} className="mb-12">
      <div className="mb-4 flex items-center gap-3">
        <h2 id={headingId} className="font-display text-2xl font-bold text-ink">
          {title}
        </h2>
        {load.status === "ready" && (
          <span className="inline-flex items-center rounded-full bg-paper-200 px-2.5 py-1 text-xs font-mono text-ink-subtle">
            {total}
          </span>
        )}
        <button
          type="button"
          onClick={onSeeAll}
          aria-label={`See all ${noun}`}
          className="ml-auto text-sm font-semibold text-brand-600 hover:underline"
        >
          See all
        </button>
      </div>

      {load.status === "loading" ? (
        <div className="py-10">
          <Spinner />
        </div>
      ) : load.status === "failed" ? (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-3 text-sm text-ink-muted">
            <p>{`Couldn't load your ${noun}. Showing sample cards.`}</p>
            <button
              type="button"
              onClick={onRetry}
              className="rounded-full border border-paper-300 px-3 py-1 text-sm font-medium text-ink-muted hover:bg-paper-100 hover:text-ink"
            >
              Try again
            </button>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
            {LANDING_SAMPLES.map((s) => (
              <LandingCard key={s.id} variant="sample" name={s.name} areas={[]} />
            ))}
          </div>
        </>
      ) : (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
          {newLabel && <LandingNewCard label={newLabel} onClick={onNew} />}
          {total === 0 ? (
            <p className="self-center text-sm text-ink-muted">{emptyText}</p>
          ) : (
            renderItems()
          )}
        </div>
      )}
    </section>
  );
};

interface HomeLandingProps {
  onNavigate(section: Section): void;
}

/**
 * Home: a quick-start strip, then the person's dashboards, projects and areas as cards with outline
 * thumbnails. Each section loads and fails on its own, so one slow or broken list never blanks the page.
 */
const HomeLanding: React.FC<HomeLandingProps> = ({ onNavigate }) => {
  const navigate = useNavigate();
  const { readOnly } = useReadOnly();
  const [projects, setProjects] = useState<Load<Project>>({ status: "loading" });
  const [areas, setAreas] = useState<Load<Area>>({ status: "loading" });
  const [lastViewed, setLastViewed] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let live = true;
    Promise.allSettled([projectsApi.list(), userMapsApi.loadGeometries(), projectsApi.getPreferences()]).then(
      ([projs, geo, prefs]) => {
        if (!live) return;
        setProjects(
          projs.status === "fulfilled" ? { status: "ready", items: projs.value } : { status: "failed" },
        );
        setAreas(geo.status === "fulfilled" ? { status: "ready", items: geo.value.maps } : { status: "failed" });
        setLastViewed(prefs.status === "fulfilled" ? prefs.value.lastDashboardId ?? null : null);
      },
    );
    return () => {
      live = false;
    };
  }, [attempt]);

  const retry = useCallback(() => {
    setProjects({ status: "loading" });
    setAreas({ status: "loading" });
    setAttempt((n) => n + 1);
  }, []);

  const areaList = useMemo(() => (areas.status === "ready" ? areas.items : []), [areas]);
  const areaById = useMemo(() => new Map(areaList.map((a) => [a.id, a])), [areaList]);

  const projectList = useMemo(
    () => (projects.status === "ready" ? newestFirst(projects.items) : []),
    [projects],
  );

  const bqById = useMemo(() => new Map(areaList.map((a) => [a.id, a.bqProjectId])), [areaList]);

  // Dashboards are Real Time cards, so only the projects whose realtime data this environment can read
  // get one. Projects and areas are not filtered. If the areas could not be loaded the filter has
  // nothing to go on and keeps them all, as it does for an unstamped area.
  const dashboardList = useMemo(() => {
    const here = projectList.filter((p) => projectRealtimeAvailability(p.areaIds, bqById) === "here");
    return [...here].sort((a, b) => (a.id === lastViewed ? -1 : b.id === lastViewed ? 1 : 0));
  }, [projectList, bqById, lastViewed]);

  const membersOf = (p: Project): Area[] =>
    p.areaIds.map((id) => areaById.get(id)).filter((a): a is Area => a !== undefined);

  const flagsOf = (members: Area[]): CountryCode[] => {
    const found = new Set(members.map((a) => areaCountry(a)).filter((c): c is CountryCode => c !== null));
    return COUNTRY_LIST.map((c) => c.code).filter((c) => found.has(c));
  };

  const areaMeta = (a: Area): React.ReactNode => {
    const country = areaCountry(a);
    const where = country ? COUNTRIES[country] : NO_COUNTRY;
    return (
      <>
        <span>{`${where.flag} ${where.label}`}</span>
        <span aria-hidden="true">·</span>
        <span>{geoTypeLabel(a.type, country)}</span>
      </>
    );
  };

  const elsewhere = (p: Project): string | null => projectDataElsewhere(p.areaIds, bqById);

  const areaCount = (n: number) => pluralize(n, "area");

  return (
    <div className="w-full">
      <div className="mb-8">
        <p className="mb-3 text-xs font-mono uppercase tracking-widest text-ink-faint">Workbench</p>
        <h1 className="mb-3 font-display text-4xl font-bold tracking-tight text-ink sm:text-5xl">Welcome back</h1>
        <p className="max-w-xl text-lg text-ink-muted">Pick up where you left off, or start something new.</p>
      </div>

      <nav aria-label="Quick start" className="mb-12 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <a href={DOCS_LINKS.quickStart} target="_blank" rel="noopener noreferrer" className={QUICK_TILE}>
          <QuickTile title="Quick start" hint="Your first request in five minutes" />
        </a>
        <a href={DOCS_LINKS.api} target="_blank" rel="noopener noreferrer" className={QUICK_TILE}>
          <QuickTile title="API reference" hint="Endpoints, parameters, examples" />
        </a>
        <a href={DOCS_LINKS.dataDelivery} target="_blank" rel="noopener noreferrer" className={QUICK_TILE}>
          <QuickTile title="Data delivery" hint="How files reach your bucket" />
        </a>
        <button type="button" onClick={() => onNavigate("status")} className={QUICK_TILE}>
          <QuickTile title="Status" hint="Which data you have, per country" />
        </button>
      </nav>

      <Grid
        id="dashboards"
        title="Dashboards"
        noun="dashboards"
        total={dashboardList.length}
        load={projects}
        onSeeAll={() => onNavigate("dashboards")}
        onRetry={retry}
        emptyText="No dashboards yet."
        newLabel={readOnly ? null : "New dashboard"}
        onNew={() => navigate("/monitor-areas-select")}
        renderItems={() =>
          dashboardList.slice(0, MAX_ITEMS).map((p) => {
            const members = membersOf(p);
            return (
              <LandingCard
                key={p.id}
                variant="dashboard"
                name={p.name}
                areas={members}
                meta={`${areaCount(p.areaIds.length)} · Real Time`}
                lastViewed={p.id === lastViewed}
                onOpen={() => navigate(`/realtime/project?project=${encodeURIComponent(p.id)}`)}
              />
            );
          })
        }
      />

      <Grid
        id="projects"
        title="Projects"
        noun="projects"
        total={projectList.length}
        load={projects}
        onSeeAll={() => onNavigate("areas")}
        onRetry={retry}
        emptyText="No projects yet."
        newLabel={readOnly ? null : "New project"}
        onNew={() => onNavigate("areas")}
        renderItems={() =>
          projectList.slice(0, MAX_ITEMS).map((p) => {
            const members = membersOf(p);
            const dataIn = elsewhere(p);
            return (
              <LandingCard
                key={p.id}
                variant="project"
                name={p.name}
                areas={members}
                meta={
                  <>
                    <span>{areaCount(p.areaIds.length)}</span>
                    {dataIn && <span className="text-xs text-ink-faint">{`Real Time data in ${dataIn}`}</span>}
                    {flagsOf(members).map((c) => (
                      <span key={c} role="img" aria-label={COUNTRIES[c].label}>
                        {COUNTRIES[c].flag}
                      </span>
                    ))}
                  </>
                }
                onOpen={() => onNavigate("areas")}
              />
            );
          })
        }
      />

      <Grid
        id="areas"
        title="Areas"
        noun="areas"
        total={areaList.length}
        load={areas}
        onSeeAll={() => onNavigate("areas")}
        onRetry={retry}
        emptyText="No areas yet."
        newLabel={readOnly ? null : "New area"}
        onNew={() => onNavigate("areas")}
        renderItems={() =>
          areaList.slice(0, MAX_ITEMS).map((a) => (
            <LandingCard
              key={a.id}
              variant="area"
              name={a.name}
              areas={[a]}
              meta={areaMeta(a)}
              onOpen={() => onNavigate("areas")}
            />
          ))
        }
      />
    </div>
  );
};

export default HomeLanding;
